// @ts-nocheck
import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getAppBaseUrl } from '@/lib/utils';

export type UserRole = 'usuario' | 'profissional' | 'administrador' | 'super_administrador' | 'teste_usuario' | 'teste_profissional';

export interface AuthUser extends User {
  role?: UserRole;
  profile?: {
    id: string;
    apelido: string;
    nome: string;
    email: string;
    celular?: string;
    avatar_url?: string | null;
  };
}

export interface AuthState {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  role: UserRole | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    role: null,
  });

  // Flag to track if this is initial load vs refetch
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fetchUserDetails = async (userId: string) => {
    try {
      console.log('Fetching user details for:', userId);
      
      // Fetch user profile - use maybeSingle to avoid errors when no data
      const { data: profile, error: profileError } = await (supabase
        .from('perfis')
        .select('*')
        .eq('id', userId)
        .maybeSingle() as any);

      if (profileError) {
        console.warn('Profile fetch error:', profileError);
      }

      // Fetch user role - use maybeSingle to avoid errors when no data
      const { data: roleData, error: roleError } = await supabase
        .from('papeis_usuario')
        .select('papel')
        .eq('usuario_id', userId)
        .maybeSingle();

      if (roleError) {
        console.warn('Role fetch error:', roleError);
      }

      const result = { 
        profile: profile || null, 
        role: (roleData?.papel || 'usuario') as UserRole 
      };
      
      console.log('User details fetched:', result);
      return result;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return { profile: null, role: 'usuario' as UserRole };
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      
      if (session?.user) {
        // Keep loading true until profile is loaded
        setState(prev => ({ 
          ...prev, 
          session, 
          loading: true, // Keep loading until profile is fetched
          user: session.user as AuthUser
        }));
        
        // Fetch user details immediately (no setTimeout)
        try {
          const { profile, role } = await fetchUserDetails(session.user.id);
          if (cancelled) return;
          setState(prev => ({
            ...prev,
            user: { ...session.user, role, profile } as AuthUser,
            role,
            loading: false, // Only set false after profile is loaded
          }));
          setIsInitialLoad(false);
        } catch (error) {
          console.error('Failed to fetch user details:', error);
          if (cancelled) return;
          setState(prev => ({
            ...prev,
            user: { ...session.user, role: 'usuario', profile: null } as AuthUser,
            role: 'usuario',
            loading: false,
          }));
          setIsInitialLoad(false);
        }
      } else {
        setState(prev => ({ ...prev, loading: false }));
        setIsInitialLoad(false);
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (cancelled) return;
        
        if (session?.user) {
          // Set loading true immediately (synchronously)
          setState(prev => ({ 
            ...prev, 
            session,
            loading: true,
            user: session.user as AuthUser
          }));
          
          // Defer the async fetchUserDetails to avoid blocking the auth callback
          setTimeout(async () => {
            if (cancelled) return;
            try {
              const { profile, role } = await fetchUserDetails(session.user.id);
              if (cancelled) return;
              setState(prev => ({
                ...prev,
                user: { ...session.user, role, profile } as AuthUser,
                role,
                loading: false,
              }));
            } catch (error) {
              console.error('Failed to fetch user details:', error);
              if (cancelled) return;
              setState(prev => ({
                ...prev,
                user: { ...session.user, role: 'usuario', profile: null } as AuthUser,
                role: 'usuario',
                loading: false,
              }));
            }
          }, 0);
        } else {
          // No session: loading becomes false immediately
          setState({
            user: null,
            session: null,
            loading: false,
            role: null,
          });
        }
      }
    );

    init();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: {
    apelido: string;
    nome: string;
    celular?: string;
  }) => {
    const redirectUrl = `${getAppBaseUrl()}/app/home`;
    
    console.log('SignUp redirect URL:', redirectUrl);
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata
      }
    });

    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${getAppBaseUrl()}/app/redefinir-senha`;
    
    console.log('ResetPassword redirect URL:', redirectUrl);
    
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { data, error };
  };

  return {
    ...state,
    signUp,
    signIn,
    signOut,
    resetPassword,
  };
}